import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { MarketplaceInstallationFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AutoSaveInput from 'modules/core/components/Inputs/AutoSaveInput'
import TextInput from 'modules/core/components/Inputs/Text'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import asyncAction from 'modules/core/lib/asyncAction'

import { useUpdateInstallation } from 'modules/marketplace/hooks/integrations/companyInstallations/useUpdateInstallation'
import {
  CompanyInstallations,
  CompanyInstallationsProvider,
} from 'modules/marketplace/store/collections/companyInstallations'
import type CodeForm from 'modules/payroll_integrations/components/Form/form'
import type { Codeable } from 'modules/payroll_integrations/lib/types'

interface Props {
  options: Array<Record<string, Codeable>>
  onSubmit: (...args: any[]) => any
  form: CodeForm
}
interface BodyProps {
  options: Array<Record<string, Codeable>>
  forms: CodeForm[]
  integrationInstallation: MarketplaceInstallationFragment | undefined
}

const Item = observer(({ onSubmit, form }: Props) => {
  return (
    <Table.Row>
      <Table.Cell colSpan={4}>
        <Box width="full" alignItems="flexStart" justifyContent="center">
          <Text.Normal>{form.get('codeable_label').value}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={4}>
        <Box width="full" alignItems="flexStart" justifyContent="center">
          <Text.Normal>{form.get('codeable_name').value}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={4}>
        <AutoSaveInput onSubmit={() => onSubmit(form)}>
          {(params: any) => <TextInput {...params} field={form.get('code')} />}
        </AutoSaveInput>
      </Table.Cell>
    </Table.Row>
  )
})

const BodyWithInstallations = ({ forms, options, integrationInstallation }: BodyProps) => {
  const { updateInstallation } = useUpdateInstallation()

  const updateInstallationData = async (form: CodeForm) => {
    if (
      !integrationInstallation?.formData ||
      !form.fields.codeable_id?.value ||
      !form.fields.code?.value
    )
      return
    const formData = JSON.parse(integrationInstallation?.formData)
    const key = form.fields.codeable_id.value
    formData[key] = form.fields.code.value
    return updateInstallation(integrationInstallation, formData)
  }

  const onSubmit = async (form: CodeForm) => {
    if (form.element.codeable_type !== 'Payroll::Concept') {
      return asyncAction(() => form.submit())
    }
    return updateInstallationData(form)
  }

  return (
    <Table.Body>
      {forms.map((form, index) => (
        <Item key={index} onSubmit={onSubmit} form={form} options={options} />
      ))}
    </Table.Body>
  )
}

// TODO: IntegrationsFramework. We have to allow a3nom to set payroll concepts and save then in the installation formData,
// this is a temporary solution as we will migrate the settings with the integrations framework.
const Body = ({ forms, options, integrationInstallation }: BodyProps) => {
  const companyInstallationCollection = useLazyRef(() => new CompanyInstallations()).current

  return (
    <CompanyInstallationsProvider collection={companyInstallationCollection}>
      <BodyWithInstallations
        forms={forms}
        options={options}
        integrationInstallation={integrationInstallation}
      />
    </CompanyInstallationsProvider>
  )
}

export default observer(Body)
